/**
 * Offline policy — the single source of truth for what may happen without a connection.
 *
 * The governing rule: **an action may be queued offline only if a delayed or duplicated
 * write is harmless.** That excludes anything that moves money, changes a contract, or
 * touches personal data, and leaves the agent's own notes and tasks — which is exactly
 * the work that happens away from the desk.
 *
 * Three tests every queueable operation passes:
 *
 *   1. **Additive or idempotent.** It appends a row, or it sets a flag that means the
 *      same thing however many times it is applied. Nothing computes a new state from
 *      the old one, because the old one may be stale by the time we reconnect.
 *   2. **No financial or contractual effect.** Marking a policy paid archives a period,
 *      advances dates and recalculates commission from *server* time — a queued copy of
 *      that decision could be days out of date and silently wrong.
 *   3. **No sensitive personal data.** Client identity fields, documents and anything
 *      under a retention obligation stay online-only, so they are never written from a
 *      cache on a lost laptop.
 *
 * Everything else keeps working offline as **read-only**, from the cached book.
 */

/* ── queueable operations ───────────────────────────────────────────────────── */

export const OFFLINE_OPERATIONS = [
  'reminder.create',
  'reminder.complete',
  'reminder.reopen',
  'activity.create',
  'claimTimeline.create',
] as const;

export type OfflineOperation = (typeof OFFLINE_OPERATIONS)[number];

export interface OfflineOperationSpec {
  /** i18n key for the human description shown in the outbox. */
  labelKey: string;
  method: 'POST' | 'PATCH';
  /** Path template; `:id` is filled from the queued entry's `targetId`. */
  path: string;
  /**
   * Whether the server dedupes replays of this operation by `clientRef`.
   * Only creates need it — the toggles are naturally idempotent.
   */
  idempotent: 'clientRef' | 'natural';
  /** Query keys to invalidate once it lands. */
  invalidates: string[];
}

export const OFFLINE_OPERATION_SPECS: Record<OfflineOperation, OfflineOperationSpec> = {
  'reminder.create': {
    labelKey: 'offline.op.reminderCreate',
    method: 'POST',
    path: '/reminders',
    idempotent: 'clientRef',
    invalidates: ['reminders', 'dashboard', 'today'],
  },
  'reminder.complete': {
    labelKey: 'offline.op.reminderComplete',
    method: 'PATCH',
    // Setting "completed" twice means the same thing as setting it once.
    path: '/reminders/:id/complete',
    idempotent: 'natural',
    invalidates: ['reminders', 'dashboard', 'today'],
  },
  'reminder.reopen': {
    labelKey: 'offline.op.reminderReopen',
    method: 'PATCH',
    path: '/reminders/:id/reopen',
    idempotent: 'natural',
    invalidates: ['reminders', 'dashboard', 'today'],
  },
  'activity.create': {
    labelKey: 'offline.op.activityCreate',
    method: 'POST',
    path: '/clients/:id/activities',
    idempotent: 'clientRef',
    invalidates: ['clients'],
  },
  'claimTimeline.create': {
    labelKey: 'offline.op.timelineCreate',
    method: 'POST',
    path: '/claims/:id/timeline',
    idempotent: 'clientRef',
    invalidates: ['claims'],
  },
};

export function isOfflineOperation(value: string): value is OfflineOperation {
  return (OFFLINE_OPERATIONS as readonly string[]).includes(value);
}

export function resolveOfflinePath(operation: OfflineOperation, targetId?: string): string {
  const { path } = OFFLINE_OPERATION_SPECS[operation];
  return targetId ? path.replace(':id', targetId) : path;
}

/* ── explicitly online-only, and why ────────────────────────────────────────── */

/**
 * Documented rather than merely absent, so the reasoning survives the next person who
 * asks "why can't I do this offline?". The UI reads these keys to explain itself
 * instead of just disabling a button.
 */
export const ONLINE_ONLY_REASONS = {
  'policy.markPaid': 'offline.blocked.financial',
  'policy.installmentPayment': 'offline.blocked.financial',
  'policy.create': 'offline.blocked.contract',
  'policy.update': 'offline.blocked.contract',
  'policy.cancel': 'offline.blocked.contract',
  'policy.endorsement': 'offline.blocked.contract',
  'claim.statusChange': 'offline.blocked.contract',
  'claim.create': 'offline.blocked.contract',
  'client.create': 'offline.blocked.personalData',
  'client.update': 'offline.blocked.personalData',
  'client.delete': 'offline.blocked.personalData',
  'document.upload': 'offline.blocked.personalData',
  'commission.settlement': 'offline.blocked.financial',
  'user.manage': 'offline.blocked.security',
  'import.run': 'offline.blocked.security',
} as const;

export type OnlineOnlyAction = keyof typeof ONLINE_ONLY_REASONS;

/* ── read-cache policy ──────────────────────────────────────────────────────── */

/**
 * Query-cache roots that may be persisted to disk for offline reading.
 *
 * Deliberately an allow-list, not a deny-list: persisting the whole cache would put the
 * audit trail, user accounts and commission statements into IndexedDB, where they would
 * outlive the session on a shared or stolen machine. Anything not named here simply is
 * not available offline, which is the safe default.
 */
export const OFFLINE_CACHEABLE_QUERY_ROOTS = [
  'clients',
  'policies',
  'claims',
  'reminders',
  'insurance-companies',
  'dashboard',
  // The daily worklist is exactly what fieldwork needs offline.
  'today',
] as const;

/**
 * Never persisted, even though the app queries them. Listed for the same reason as
 * ONLINE_ONLY_REASONS — so the exclusion is a decision, not an oversight.
 */
export const OFFLINE_EXCLUDED_QUERY_ROOTS = [
  'audit', // regulated record; belongs on the server only
  'users', // account data
  'settlements', // financial reconciliation
  'commissions',
  'notifications',
  'search', // transient, and re-running it offline would mislead
] as const;

export function isCacheableQueryRoot(root: unknown): boolean {
  return (
    typeof root === 'string' &&
    (OFFLINE_CACHEABLE_QUERY_ROOTS as readonly string[]).includes(root)
  );
}

/* ── outbox entry ───────────────────────────────────────────────────────────── */

export type OutboxStatus = 'pending' | 'syncing' | 'failed';

export interface OutboxEntry {
  /** Client-generated UUID — also the server-side idempotency key for creates. */
  id: string;
  operation: OfflineOperation;
  /** Path parameter, e.g. the client or claim the note belongs to. */
  targetId?: string;
  payload: Record<string, unknown>;
  /** Short human summary shown in the outbox list, e.g. the reminder's title. */
  summary: string;
  status: OutboxStatus;
  attempts: number;
  createdAt: string;
  lastAttemptAt?: string;
  /** Set when the server rejected it for a reason retrying will not fix. */
  error?: string;
  /** The signed-in user at queue time — a queue is never drained as someone else. */
  userId: string;
}

/** Exponential backoff with a ceiling, so a long outage does not hammer on reconnect. */
export function retryDelayMs(attempts: number): number {
  return Math.min(30_000, 1_000 * 2 ** Math.max(0, attempts - 1));
}

/** After this many failures an entry stops retrying and waits for a human decision. */
export const MAX_SYNC_ATTEMPTS = 5;
